import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Plus, X, MapPin, Building2, Trash2, ArrowUpRight, FileDown } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/format";
import DeletePropertyModal from "@/components/DeletePropertyModal";

const IMG_DEFAULTS = [
  "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&w=800&q=75",
  "https://images.unsplash.com/photo-1516501312919-d0cb0b7b60b8?crop=entropy&cs=srgb&fm=jpg&w=800&q=75",
];

export default function Properties() {
  const [items, setItems] = useState([]);
  const [statsById, setStatsById] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // property object pending delete
  const [form, setForm] = useState({ name: "", address: "", image_url: IMG_DEFAULTS[0], notes: "" });

  const load = async () => {
    const { data } = await api.get("/properties");
    setItems(data);
    const results = await Promise.all(
      data.map((p) => api.get(`/dashboard?property_id=${p.id}`).then((r) => [p.id, r.data]).catch(() => [p.id, null])),
    );
    const map = {};
    for (const [id, d] of results) map[id] = d;
    setStatsById(map);
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/properties", form);
      toast.success("Property added");
      setForm({ name: "", address: "", image_url: IMG_DEFAULTS[0], notes: "" });
      setShowForm(false);
      load();
    } catch { toast.error("Could not save property"); }
  };

  const del = async (e, prop) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteTarget(prop);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await api.delete(`/properties/${deleteTarget.id}`);
    toast.success("Property deleted");
    setDeleteTarget(null);
    load();
  };

  // Minimal sidebar: Properties active + "Add Property" button underneath
  const navItems = [
    { to: "/", label: "Properties", icon: Building2, testid: "nav-properties", exact: true },
  ];
  const sidebarExtra = (
    <button
      onClick={() => setShowForm(true)}
      data-testid="sidebar-add-property-btn"
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm border border-dashed border-slate-300 text-slate-600 hover:border-slate-900 hover:text-slate-900 hover:bg-slate-50 transition-colors duration-200"
    >
      <Plus className="w-4 h-4" strokeWidth={2} />
      <span className="font-medium">Add property</span>
    </button>
  );

  return (
    <Layout
      title="Properties"
      subtitle="Every building you manage. Click a card to open its full portfolio view."
      navItems={navItems}
      sidebarExtra={sidebarExtra}
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              try {
                const token = localStorage.getItem("roost_token");
                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portfolio/export.csv`, {
                  credentials: "include",
                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (!res.ok) throw new Error("CSV error");
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `portfolio-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                toast.success("Portfolio exported");
              } catch { toast.error("Could not export portfolio CSV"); }
            }}
            disabled={items.length === 0}
            data-testid="portfolio-export-csv-btn"
            className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FileDown className="w-4 h-4" /> Export portfolio CSV
          </button>
          <button
            onClick={() => setShowForm(true)}
            data-testid="add-property-btn"
            className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors"
          >
            <Plus className="w-4 h-4" /> Add property
          </button>
        </div>
      }
    >
      {items.length === 0 ? (
        <div className="card-flat p-10 text-center fade-up">
          <Building2 className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <div className="font-display text-lg font-semibold">No properties yet</div>
          <p className="text-sm text-slate-500 mt-1 mb-5">Add your first building to start collecting rent.</p>
          <button
            onClick={() => setShowForm(true)}
            data-testid="empty-add-property-btn"
            className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
          >
            <Plus className="w-4 h-4" /> Add property
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {items.map((p, idx) => {
            const s = statsById[p.id];
            return (
              <Link
                key={p.id}
                to={`/properties/${p.id}`}
                data-testid={`property-card-${p.id}`}
                className="card-flat overflow-hidden fade-up group hover:-translate-y-[2px] transition-transform block"
                style={{ animationDelay: `${idx * 40}ms` }}
              >
                {p.image_url && (
                  <div className="aspect-[16/9] overflow-hidden bg-slate-100 relative">
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute top-3 right-3 flex gap-1">
                      <button
                        onClick={(e) => del(e, p)}
                        data-testid={`property-delete-${p.id}`}
                        className="w-8 h-8 rounded-lg bg-white/90 backdrop-blur border border-slate-200 grid place-items-center text-slate-500 hover:text-red-600 hover:bg-white transition"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-display font-semibold text-lg tracking-tight truncate">{p.name}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-1 mt-1 truncate">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{p.address}</span>
                      </div>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-slate-900 transition-colors flex-shrink-0" />
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-2 pt-4 border-t border-slate-100">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400">Gross</div>
                      <div className="font-display font-semibold text-sm mt-0.5">{formatCurrency(s?.gross_rental || 0)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400">Nett</div>
                      <div className="font-display font-semibold text-sm mt-0.5 text-emerald-700">{formatCurrency(s?.nett_rent || 0)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400">Outstanding</div>
                      <div className="font-display font-semibold text-sm mt-0.5 text-red-700">{formatCurrency(s?.outstanding || 0)}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
                    <span>{s?.counts?.tenants || 0} tenants</span>
                    <span>·</span>
                    <span>{s?.counts?.active_contracts || 0} active leases</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {showForm && (
        <Modal onClose={() => setShowForm(false)} title="Add property">
          <form onSubmit={save} className="space-y-4" data-testid="property-form">
            <Field label="Name">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="property-name-input" className={inputCls} placeholder="Sea Point Apartments" />
            </Field>
            <Field label="Address">
              <input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="property-address-input" className={inputCls} placeholder="12 Regent Rd, Sea Point, Cape Town" />
            </Field>
            <Field label="Image URL">
              <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="property-image-input" className={inputCls} />
              <div className="flex gap-2 mt-2">
                {IMG_DEFAULTS.map((u) => (
                  <button key={u} type="button" onClick={() => setForm({ ...form, image_url: u })} className={`w-16 h-10 rounded-md overflow-hidden border-2 transition ${form.image_url === u ? "border-slate-900" : "border-transparent"}`}>
                    <img src={u} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Notes">
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} h-24 py-2`} />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Cancel</button>
              <button type="submit" data-testid="property-save-btn" className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">Save property</button>
            </div>
          </form>
        </Modal>
      )}

      {deleteTarget && (
        <DeletePropertyModal
          property={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirmDelete={confirmDelete}
        />
      )}
    </Layout>
  );
}

const inputCls = "w-full h-10 px-3 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 text-sm";

export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

export function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="modal-backdrop">
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-lg fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="font-display font-semibold text-lg">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 transition"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
